// JOBE-0044.10: TypeValuePopover — multi-select of entity types.
// Reads from EntityTypesContext (JOBE-0044.6) for top-level entity
// types and DocumentKindsContext (JOBE-0044.10.2) for document
// sub-kinds. Both contexts are owned by LibraryListPage's providers;
// reading them here (vs. accepting prop-drilled lists) keeps the
// loading / error UX surfaceable without the parent collapsing
// non-`ready` states to `[]`.
//
// PR #699 review: the previous prop-drill (LibraryListPage → ChipRow →
// FilterComposer → TypeValuePopover) collapsed every non-`ready` kinds
// state to `[]`, silently rendering an entity-types-only picker during
// loading/error with no retry path. Reading the context directly
// symmetrises with the entity-types read and surfaces both states
// natively.

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EntityTypesContext } from '../../EntityTypesContext.js';
import { DocumentKindsContext } from '../../DocumentKindsContext.js';

export function TypeValuePopover({
  initialValues = [],
  onSubmit,
  onCancel,
}) {
  const ctx = useContext(EntityTypesContext);
  const kindsCtx = useContext(DocumentKindsContext);
  const [selected, setSelected] = useState(() => new Set(initialValues));
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (onCancel) onCancel();
    }
  };

  const types = Array.isArray(ctx.types) ? ctx.types : [];
  const documentKinds = kindsCtx.status === 'ready' && Array.isArray(kindsCtx.kinds)
    ? kindsCtx.kinds
    : [];
  const rows = useMemo(() => {
    const out = [];
    for (const t of types) {
      out.push({
        kind: t.kind,
        label: t.displayName || t.kind,
        source: t.kindSource,
      });
    }
    for (const k of documentKinds) {
      out.push({
        kind: k.kind,
        label: k.kind,
        source: k.kindSource || 'core',
      });
    }
    return out;
  }, [types, documentKinds]);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((r) =>
      `${r.kind} ${r.label} ${r.source}`.toLowerCase().includes(q),
    );
  }, [rows, query]);

  const toggle = (kind) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  const submit = () => {
    if (selected.size === 0) return;
    if (onSubmit) onSubmit(Array.from(selected));
  };

  // Aggregate the two contexts' status for the row-list area. The
  // popover renders the entity-types' status as the dominant signal
  // (entity types are the primary list); the document-kinds status
  // surfaces as a secondary, additive row so users see "kinds loading"
  // / "couldn't load kinds" alongside any types that did load.
  const showTypesLoading = ctx.status === 'loading';
  const showTypesError = ctx.status === 'error';
  const showKindsLoading = ctx.status === 'ready' && kindsCtx.status === 'loading';
  const showKindsError = ctx.status === 'ready' && kindsCtx.status === 'error';

  return (
    <div
      className="library-composer-popover library-composer-types"
      role="dialog"
      aria-label="Pick types"
      onKeyDown={onKeyDown}
    >
      <div className="library-composer-popover-head">
        <input
          ref={searchRef}
          type="text"
          className="library-composer-search"
          placeholder="Find a type…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search types"
        />
      </div>
      <div className="library-composer-popover-body">
        {showTypesLoading && (
          <p className="library-composer-empty">loading…</p>
        )}
        {showTypesError && (
          <p className="library-composer-empty">couldn't load types</p>
        )}
        {ctx.status === 'ready' && filtered.length === 0
          && !showKindsLoading && !showKindsError && (
            <p className="library-composer-empty">no matches</p>
          )}
        {ctx.status === 'ready' && (filtered.length > 0
          || showKindsLoading || showKindsError) && (
          <ul className="library-composer-type-list">
            {filtered.map((r) => (
              <li key={r.kind}>
                <label className="library-composer-row">
                  <input
                    type="checkbox"
                    checked={selected.has(r.kind)}
                    onChange={() => toggle(r.kind)}
                  />
                  <span className="library-composer-row-label">{r.label}</span>
                  <span
                    className={
                      'library-composer-row-pill '
                      + (r.source === 'user'
                        ? 'library-composer-row-pill-user'
                        : 'library-composer-row-pill-core')
                    }
                  >
                    {r.source || 'core'}
                  </span>
                </label>
              </li>
            ))}
            {showKindsLoading && (
              <li>
                <p
                  className="library-composer-empty library-composer-kinds-loading"
                  aria-busy="true"
                >
                  loading kinds…
                </p>
              </li>
            )}
            {showKindsError && (
              <li>
                <p className="library-composer-empty library-composer-kinds-error">
                  couldn't load kinds ·{' '}
                  <button
                    type="button"
                    className="library-composer-kinds-retry"
                    onClick={() => kindsCtx.retry && kindsCtx.retry()}
                  >
                    retry
                  </button>
                </p>
              </li>
            )}
          </ul>
        )}
      </div>
      <div className="library-composer-popover-foot">
        <button
          type="button"
          className="library-composer-submit"
          onClick={submit}
          disabled={selected.size === 0}
        >
          Add chip
        </button>
      </div>
    </div>
  );
}
